import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthContextType, CompanyType, COMPANY_TYPE_CONFIG } from '../types/auth';
import { navigate } from '../utils/navigation';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.port === '3000' ? `http://${window.location.hostname}:8000` : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'));

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('fraudguard_user');
    const token = localStorage.getItem('fraudguard_token');
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('fraudguard_user');
        localStorage.removeItem('fraudguard_token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) return false;

      const data = await res.json();

      // ✅ normalize company_type من الباك اند
      const rawType = (data.user.company_type ?? '').toLowerCase().trim();
      let companyType: CompanyType;

      if (rawType === 'provider') {
        companyType = 'provider';
      } else if (rawType === 'payer') {
        companyType = 'payer';
      } else if (rawType === 'post-office' || rawType === 'post_office' || rawType === 'postoffice') {
        companyType = 'post-office';
      } else if (rawType === 'admin') {
        companyType = 'admin';
      } else {
        companyType = 'admin'; // fallback
      }

      const userData: User = {
        id: data.user.id.toString(),
        email: data.user.email,
        name: data.user.company_name || data.user.email.split("@")[0],
        role: 'admin',
        title: 'System Administrator',
        company_type: companyType,
        company_name: data.user.company_name,
      };

      setUser(userData);
      localStorage.setItem('fraudguard_token', data.access_token);
      localStorage.setItem('fraudguard_user', JSON.stringify(userData));
      navigate('/');

      return true;

    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('fraudguard_user');
    localStorage.removeItem('fraudguard_token');
    navigate('/login');
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(20px)', borderRadius: '24px', border: '1px solid rgba(56,189,248,0.2)', padding: '48px', textAlign: 'center', maxWidth: '420px', width: '100%' }}>
          <div style={{ width: '64px', height: '64px', border: '3px solid rgba(56,189,248,0.3)', borderTopColor: '#38bdf8', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 24px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#f1f5f9', marginBottom: '8px' }}>Loading System</h3>
          <p style={{ fontSize: '14px', color: '#94a3b8' }}>Initializing...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthProvider;
